import { taxProductAreaByState } from "../auth/apiProduct";

// Order totals of the existing Checkout (CheckoutProd.js), as pure functions.
// The Cart Check-in modal computes its per-line and selected totals with these
// SAME functions, so what the buyer sees before Checkout is exactly what the
// Checkout modal shows (and sends) for the same lines and destination.
//
//   item subtotal = (in stock ? 1 : 0) x count x price x (100 - salePrice) / 100
//   shipping      = the backend estimate's chargeable amounts (never a guess)
// Tax and the final total are NEVER computed here: they come only from the
// server checkout quote (POST /order/quote), see ./checkoutTaxDisplay.jsx.

const finite = (n) => (Number.isFinite(n) ? n : 0);

// A line whose stock is exactly 0 adds nothing (CheckoutProd `checkQuantity`).
export const stockFactor = (quantity) => (quantity === 0 ? 0 : 1);

export const unitPriceOf = (product) =>
  (Number(product?.price) || 0) * (100 - (Number(product?.salePrice) || 0)) / 100;

export const lineItemsSubtotal = (product) =>
  finite(stockFactor(product?.quantity) * (Number(product?.count) || 0) * unitPriceOf(product));

export const itemsTotal = (products = []) =>
  (Array.isArray(products) ? products : []).reduce((acc, p) => acc + lineItemsSubtotal(p), 0);

export const roundMoney = (n) => Number((Number(n) || 0).toFixed(2));

export const formatMoney = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Destination tax zone of a checkout address (CheckoutProd `Saddress` shape),
// for display context only (never a charged rate): {} when the address has no
// country/postal code yet or the lookup fails.
export const fetchDestinationTax = async (address) => {
  const country = address?.country;
  const postalCode = address?.postal_code || address?.postalCode;
  const regionCode = address?.state || address?.State || address?.stateCode;
  if (!country || !postalCode) return {};
  try {
    const data = await taxProductAreaByState(country, postalCode, regionCode);
    return data?.zoneTax || {};
  } catch {
    return {};
  }
};

// A backend shipping estimate (useShippingEstimate) narrowed to some of its
// lines, with totals combined exactly like the estimator does
// (server_elakay/elakay_services/shipping/estimator/index.js combineCost +
// chargeableAmount). Shipping is priced per line, so the sum for a selection
// is what a checkout of only those lines is charged.
export const selectShippingEstimate = (estimate, indexes = []) => {
  if (estimate?.status !== "ready" || !estimate.data) {
    return { status: estimate?.status || "idle", updating: Boolean(estimate?.updating), data: null };
  }
  const lines = indexes.map((i) => estimate.data.lines?.[i]).filter(Boolean);
  const currency = lines.find((l) => l.cost?.currency)?.cost.currency || "USD";
  let status = "fixed";
  if (lines.length > 0 && lines.every((l) => l.cost?.isFree)) status = "free";
  else if (lines.some((l) => l.cost?.status === "unavailable")) status = "unavailable";
  const chargeableAmount = Math.round(lines
    .filter((l) => l.cost?.chargeable && l.cost?.status === "fixed")
    .reduce((s, l) => s + (Number(l.cost.amount) || 0), 0) * 100) / 100;
  // Packages keep only the selected lines (a package left empty disappears).
  const chosen = new Set(indexes);
  const groups = (estimate.data.groups || []).map((g) => {
    const keep = (g.lineIndexes || []).map((lineIndex, k) => ({ lineIndex, k })).filter(({ lineIndex }) => chosen.has(lineIndex));
    if (!keep.length) return null;
    const kept = new Set(keep.map(({ k }) => k));
    return { ...g, lineIndexes: keep.map(({ lineIndex }) => lineIndex), products: (g.products || []).filter((_, k) => kept.has(k)) };
  }).filter(Boolean);
  return {
    status: "ready",
    updating: false,
    data: {
      ...estimate.data,
      lines,
      groups,
      totals: { status, isFree: status === "free", currency, chargeableAmount, chargeableCents: Math.round(chargeableAmount * 100) },
    },
  };
};
